import { round1 } from "@/bizminer/lib/heatmap/scoreMath";
import {
  type HeatmapTileStatus,
  tileBgColor,
} from "@/bizminer/lib/heatmap/tileColors";
import type { HeatmapScore, HeatmapTile } from "@/bizminer/lib/heatmap/heatmapTypes";
import { cn } from "@/lib/utils";

export default function HeatmapTileButton({
  tile,
  score,
  status,
  dimmed,
  selected,
  onSelect,
  companyLabel,
  peersLabel,
}: {
  tile: HeatmapTile;
  score: HeatmapScore;
  status: HeatmapTileStatus;
  dimmed: boolean;
  selected: boolean;
  onSelect: (tileId: string) => void;
  companyLabel: string;
  peersLabel: string;
}) {
  // These tiles use fixed heatmap colors (same in light & dark mode). We keep
  // the text color hardcoded to ensure contrast against those backgrounds.
  const companyValue = Number.isFinite(score.company)
    ? round1(score.company as number).toFixed(1)
    : "—";
  const peersValue = Number.isFinite(score.peers)
    ? round1(score.peers as number).toFixed(1)
    : "—";
  const isNoData = status === "no_data";

  return (
    <button
      type="button"
      onClick={() => onSelect(tile.id)}
      className={cn(
        "rounded-lg border transition-opacity shadow-sm overflow-hidden",
        dimmed && "opacity-35",
        selected &&
          "ring-2 ring-slate-900 ring-offset-2 ring-offset-background",
        // Hardcoded slate-900: fixed contrast against heatmap tile backgrounds, not theme-dependent.
        isNoData
          ? "border-border/60 text-foreground"
          : "border-slate-900/10 text-slate-900",
        "h-[130px] grid grid-rows-[55px_1fr]",
      )}
      style={{ backgroundColor: tileBgColor(status) }}
      aria-label={`${tile.label}: ${companyLabel} ${companyValue}, ${peersLabel} ${peersValue}`}
    >
      <div className="px-4 pt-3">
        <div className="text-sm font-extrabold leading-snug line-clamp-2">
          {tile.label}
        </div>
      </div>

      <div className="px-4 pb-3">
        <div className="border-t border-[color:var(--bmad-score-divider)] pt-3">
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
            <div className="grid gap-1 min-w-0">
              <div className="text-xl font-extrabold tabular-nums leading-none">
                {companyValue}
              </div>
            </div>
            <div className="w-px h-10 bg-[color:var(--bmad-score-divider)]" />
            <div className="grid gap-1 min-w-0">
              <div className="text-xl font-extrabold tabular-nums leading-none">
                {peersValue}
              </div>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
